import { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarCheck, LogIn } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ParticleCanvas } from "../components/landing/ParticleCanvas";
import { IntroFlow } from "../components/landing/IntroFlow";
import { HeroSection } from "../components/landing/HeroSection";
import { PainPointsSection } from "../components/landing/PainPointsSection";
import { ThreeStepsSection } from "../components/landing/ThreeStepsSection";
import { BenefitsSection } from "../components/landing/BenefitsSection";
import { SocialProofSection } from "../components/landing/SocialProofSection";
import { ComparisonSection } from "../components/landing/ComparisonSection";
import { SecuritySection } from "../components/landing/SecuritySection";
import { PricingSection } from "../components/landing/PricingSection";
import { FaqSection, FAQS } from "../components/landing/FaqSection";
import { SolutionsSection } from "../components/landing/SolutionsSection";
import { DemoRequestSection } from "../components/landing/DemoRequestSection";
import { FinalCtaSection } from "../components/landing/FinalCtaSection";
import { LandingFooter } from "../components/landing/LandingFooter";
import { LANDING_JSON_LD, useSeo } from "../lib/seo";
import { BrandLogo } from "../components/BrandLogo";
import "../styles/landingSections.css";

const MODULES = [
  {
    num: "01", title: "Amministrazione e controllo",
    stage: "Il flusso sconnesso si ordina: le particelle formano il diagramma a barre della direzione.",
    desc: "Il quadro di comando dell'officina: sedi, clienti e utenti con permessi per reparto, report di produzione con ore previste contro ore reali e — con il Controllo di gestione — margine di commessa, costi consuntivi e obiettivi con semafori.",
    bullets: [
      "Sedi, anagrafica clienti con scheda e storico, utenti e ruoli",
      "Report produzione: ore, sforamenti e saturazione delle macchine",
      "Dashboard direzionale con 12 KPI, obiettivi e semafori (Premium)",
      "Margine per commessa e cliente con materiale e utensili consuntivati (Premium)",
    ],
    plan: "Sedi, clienti e report produzione in Free e Standard · Controllo di gestione con Premium",
  },
  {
    num: "02", title: "Logistica generale e utensili",
    stage: "Le particelle si compattano: un inserto di tornitura rivestito.",
    desc: "Catalogo tecnico, giacenze in tempo reale, movimenti tracciati: utensili, attacchi base, materie prime, ricambi, semilavorati e attrezzature di staffaggio hanno posizione fisica, vita utile e costo sotto controllo. L'import Excel del catalogo ti fa partire in giornata.",
    bullets: [
      "Catalogo utensili e attacchi base con schede tecniche e foto",
      "Magazzini, posizioni, inventario e movimenti multi-sede",
      "Semilavorati e attrezzature di staffaggio con cartellino QR",
      "Scorte minime, consumi e vita utile per tagliente",
    ],
    plan: "Con limiti nel piano Free · senza limiti dal piano Easy Tool",
    link: "/soluzioni/utensili",
  },
  {
    num: "03", title: "Acquisti e fornitori",
    stage: "L'inserto diventa un ordine: le barre arrivano sul pallet.",
    desc: "Il ciclo passivo completo, non una lista della spesa: fornitori con listini e lead time, proposte di riordino su tutti i tipi di articolo, ordini numerati con IVA e stampa, arrivo merce che carica il magazzino e scrive il movimento.",
    bullets: [
      "Fornitori con listini importabili, condizioni di pagamento e puntualità calcolata",
      "Proposte di riordino su utensili, attacchi base, materie prime e ricambi",
      "Ordini ODA numerati, stampa PDF con carta intestata e invio email",
      "Arrivo merce parziale o a saldo che aggiorna giacenze e registro movimenti",
    ],
    plan: "Solo con i piani Standard e Premium",
    link: "/soluzioni/acquisti",
  },
  {
    num: "04", title: "Produzione",
    stage: "L'inserto avanza lungo la barra cilindrica: la passata è iniziata.",
    desc: "Ordini cliente, ordini di produzione e fasi su una timeline interattiva: carichi di macchine e operatori, colli di bottiglia in evidenza, avanzamento in officina con ore reali, pezzi buoni, scarti e rilavorati.",
    bullets: [
      "Ordini cliente, OdP con ciclo congelato, lotti e consegne con DDT",
      "Planner settimanale, Kanban e conflitti di capacità in evidenza",
      "Avanzamento officina con cronometro delle ore reali",
      "Macchine con curve coppia/potenza, idoneità e materiali",
    ],
    plan: "Con limiti nel piano Free · senza limiti dal piano Standard",
    link: "/soluzioni/produzione",
  },
  {
    num: "05", title: "Qualità",
    stage: "L'inserto ha finito la passata: sulla barra restano quote e tolleranze geometriche.",
    desc: "Piani di controllo dentro la scheda del componente, quote misurate con lo strumento tarato e non conformità collegate a commesse e fasi: le prove documentate che chiedono i clienti e gli enti di certificazione.",
    bullets: [
      "Piani di controllo per fase, con quote lineari, Ø e raggi",
      "Controlli digitali, andamento delle quote e strumenti con tarature",
      "Non conformità valorizzate e azioni correttive 8D",
      "Report di direzione e audit log pronti per ISO 9001 e IATF",
    ],
    plan: "Solo con il piano Premium",
    link: "/soluzioni/qualita",
  },
  {
    num: "06", title: "Preventivazione",
    stage: "Quote e passate diventano lavorazioni con tempi e costi.",
    desc: "Dal disegno al prezzo: tempi calcolati dai parametri di taglio o dalle fasi del tuo ciclo, tariffa oraria per ogni macchina, costo industriale, margine e prezzo proposto mentre scrivi. Offerta in PDF per il cliente, stampa interna con i costi, confronto con il consuntivo.",
    bullets: [
      "Preventivo rapido parametrico o analitico dal ciclo del componente",
      "Costo macchina e costi operativi separati, materiale e utensili inclusi",
      "Fasi esterne, offerta PDF e revisioni immutabili",
      "Confronto fra ore preventivate e ore reali di officina",
    ],
    plan: "Solo con il piano Premium",
    link: "/soluzioni/preventivazione",
  },
];

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};

const LANDING_SCHEMA = [LANDING_JSON_LD, FAQ_JSON_LD];

export default function LandingPage() {
  const progressRef = useRef(0);
  const rootRef = useRef(null);
  const [leaving, setLeaving] = useState("");
  const navigate = useNavigate();

  useSeo({
    title: "ToolsValue | Software gestionale per officine di meccanica di precisione",
    description: "Utensili, magazzino, acquisti, produzione, qualità e preventivi in un'unica piattaforma per officine di precisione: giacenze e posizioni, planner con ore reali, costo per pezzo e margine di commessa. Piano Free e demo di 20 minuti.",
    path: "/",
    jsonLd: LANDING_SCHEMA,
  });

  const startTransition = (path: string) => (event) => {
    event.preventDefault();
    if (!leaving) setLeaving(path);
  };

  useEffect(() => {
    const onScroll = () => {
      const sections = Array.from(rootRef.current.querySelectorAll(".landing-module")) as HTMLElement[];
      if (!sections.length) return;
      const centers = sections.map((node) => node.offsetTop + node.offsetHeight / 2 - window.innerHeight / 2);
      const step = centers.length > 1 ? centers[1] - centers[0] : window.innerHeight;
      const anchors = [Math.max(centers[0] - step, 0), ...centers];
      const y = window.scrollY;
      let stage;
      if (y <= anchors[0]) stage = anchors[0] > 0 ? y / anchors[0] : 1;
      else {
        const index = anchors.findIndex((anchor) => y <= anchor);
        stage = index <= 0
          ? anchors.length
          : index + (y - anchors[index - 1]) / (anchors[index] - anchors[index - 1]);
      }
      progressRef.current = Math.min(Math.max(stage / anchors.length, 0), 1);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [progressRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add("in-view"); }),
      { threshold: 0.15 },
    );
    rootRef.current.querySelectorAll(".landing-reveal").forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="landing-page" ref={rootRef} data-testid="landing-page">
      <ParticleCanvas progressRef={progressRef} />

      <header className="landing-topbar" data-testid="landing-topbar">
        <div className="landing-brand">
          <BrandLogo size={44} className="landing-brand-logo" />
          <span>OFFICINA<b>TOOL CONTROL</b></span>
        </div>
        <nav className="landing-topbar-actions">
          <a className="landing-ghost-link landing-nav-anchor" href="#come-funziona" data-testid="landing-nav-how">Come funziona</a>
          <a className="landing-ghost-link" href="#soluzioni" data-testid="landing-nav-solutions">Moduli</a>
          <a className="landing-ghost-link landing-nav-anchor" href="#prezzi" data-testid="landing-nav-pricing">Prezzi</a>
          <a className="landing-ghost-link landing-nav-anchor" href="#faq" data-testid="landing-nav-faq">Domande</a>
          <Link className="landing-ghost-link" to="/blog" data-testid="landing-nav-blog">Blog</Link>
          <Link className="landing-ghost-link is-mobile-keep" to="/login" onClick={startTransition("/login")} data-testid="landing-login-button">
            <LogIn size={14} />Accedi
          </Link>
          <a className="landing-login-btn" href="#demo" data-testid="landing-nav-demo">
            <CalendarCheck size={15} />Prenota demo
          </a>
        </nav>
      </header>

      <HeroSection onNavigate={startTransition} />

      <div className="landing-modules-intro landing-reveal" id="moduli" data-testid="landing-modules-intro">
        <p className="landing-module-num">SEI MODULI <i /> UN UNICO FLUSSO</p>
        <h2>Il pezzo che nasce, modulo per modulo</h2>
        <p className="landing-desc">
          Scorri: le particelle si ricompongono a ogni modulo, dal dato di direzione all'inserto, dall'ordine
          al fornitore alla passata, dalla quota controllata al prezzo.
        </p>
      </div>

      {MODULES.map((module, index) => (
        <section className="landing-module" key={module.num} data-testid={`landing-module-${index + 1}`}>
          <div className="landing-module-copy landing-reveal">
            <p className="landing-module-num">{module.num} <i /> MODULO</p>
            <h2>{module.title}</h2>
            <p className={`landing-module-plan${module.plan.startsWith("Solo") ? " is-premium" : ""}`} data-testid={`landing-module-plan-${index + 1}`}>{module.plan}</p>
            <p className="landing-stage" data-testid={`landing-stage-${index + 1}`}>{module.stage}</p>
            <p className="landing-desc">{module.desc}</p>
            <ul className="landing-bullets">
              {module.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
            </ul>
            {module.link && (
              <Link className="landing-module-link" to={module.link} data-testid={`landing-module-link-${index + 1}`}>
                Scopri il modulo <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </section>
      ))}

      <PainPointsSection />
      <ThreeStepsSection />
      <BenefitsSection />
      <SocialProofSection />
      <ComparisonSection />
      <SecuritySection />
      <SolutionsSection />
      <PricingSection />
      <FaqSection />
      <DemoRequestSection />
      <FinalCtaSection onNavigate={startTransition} />
      <LandingFooter onNavigate={startTransition} />

      <div className="landing-mobile-cta" data-testid="landing-mobile-cta">
        <a className="landing-login-btn" href="#demo">Prenota una demo</a>
        <Link className="landing-outline-btn" to="/registra-azienda" onClick={startTransition("/registra-azienda")}>
          Prova gratis
        </Link>
      </div>

      {leaving && <IntroFlow onDone={() => navigate(leaving, { state: { fromLanding: true } })} />}
    </main>
  );
}
